// Settings that belong to this device, not to the account: the theme, the send
// key, widths of the sidebar and the list (a keyboard or a screen size differs
// per device).
//
// Promise:
// - `defineDeviceSetting` names a localStorage key, how to read a stored string
//   and the default. Keys keep the names the old web used, so a browser keeps
//   its choices.
// - `useDeviceSetting` re-renders on a change in this tab and in other tabs
//   (the `storage` event). When storage cannot be written (private mode), the
//   value still applies to this tab.
// - A new key is listed in keys.ts (the desktop carries those between its
//   screens); `definedDeviceSettingKeys` is what the tests compare it with.
import { useCallback, useSyncExternalStore } from "react";

const defined = new Set<string>();

/** Every key defined so far (the modules that define them loaded). */
export function definedDeviceSettingKeys(): string[] {
  return [...defined];
}

export interface DeviceSetting<T> {
  key: string;
  read(): T;
  write(value: T): void;
  subscribe(onChange: () => void): () => void;
}

const CHANGE_EVENT = "trameria-device-setting";

export function defineDeviceSetting<T>(spec: {
  key: string;
  parse(raw: string | null): T;
  serialize?(value: T): string;
}): DeviceSetting<T> {
  defined.add(spec.key);
  let unsaved: { value: T } | null = null;
  const serialize = spec.serialize ?? ((v: T) => String(v));
  const readRaw = (): string | null => {
    try {
      return localStorage.getItem(spec.key);
    } catch {
      return null;
    }
  };
  // useSyncExternalStore needs the same object back while nothing changed.
  let cache: { raw: string | null; value: T } | null = null;
  return {
    key: spec.key,
    read() {
      if (unsaved) return unsaved.value;
      const raw = readRaw();
      if (!cache || cache.raw !== raw) cache = { raw, value: spec.parse(raw) };
      return cache.value;
    },
    write(value) {
      try {
        localStorage.setItem(spec.key, serialize(value));
        unsaved = null;
      } catch {
        unsaved = { value };
      }
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: spec.key }));
    },
    subscribe(onChange) {
      const onStorage = (e: StorageEvent) => {
        if (e.key === null || e.key === spec.key) onChange();
      };
      const onLocal = (e: Event) => {
        if ((e as CustomEvent).detail === spec.key) onChange();
      };
      window.addEventListener("storage", onStorage);
      window.addEventListener(CHANGE_EVENT, onLocal);
      return () => {
        window.removeEventListener("storage", onStorage);
        window.removeEventListener(CHANGE_EVENT, onLocal);
      };
    },
  };
}

export function useDeviceSetting<T>(
  setting: DeviceSetting<T>,
): [T, (value: T) => void] {
  const value = useSyncExternalStore(
    setting.subscribe,
    setting.read,
    setting.read,
  );
  const set = useCallback((v: T) => setting.write(v), [setting]);
  return [value, set];
}
